import Database from '../types/Database'

/**
 * Validates the account name field.
 * Must be 1–100 characters (not empty, not over 100).
 *
 * @param name - The account name to validate
 * @returns An error message string if invalid, or null if valid
 */
export function validateAccountName(name: string): string | null {
  if (!name || name.trim().length === 0) {
    return 'Account name is required'
  }
  if (name.length > 100) {
    return 'Account name must not exceed 100 characters'
  }
  return null
}

/**
 * Validates the BSB field.
 * Must be exactly 6 numeric digits.
 *
 * @param bsb - The BSB string to validate
 * @returns An error message string if invalid, or null if valid
 */
export function validateBsb(bsb: string): string | null {
  if (!/^\d{6}$/.test(bsb)) {
    return 'BSB must be exactly 6 digits'
  }
  return null
}

/**
 * Validates the account number field.
 * Must be 6–10 numeric digits.
 *
 * @param accountNumber - The account number string to validate
 * @returns An error message string if invalid, or null if valid
 */
export function validateAccountNumber(accountNumber: string): string | null {
  if (!/^\d{6,10}$/.test(accountNumber)) {
    return 'Account number must be between 6 and 10 digits'
  }
  return null
}

/**
 * Validates all bank details fields.
 * Returns a map of field names to error messages for any invalid fields,
 * or null if all fields are valid.
 *
 * @param details - The BankDetails object to validate
 * @returns A Record<string, string> of field errors, or null if all valid
 */
export function validateBankDetails(
  details: Database.BankDetails
): Record<string, string> | null {
  const errors: Record<string, string> = {}

  const accountNameError = validateAccountName(details.accountName)
  if (accountNameError) {
    errors.accountName = accountNameError
  }

  const bsbError = validateBsb(details.bsb)
  if (bsbError) {
    errors.bsb = bsbError
  }

  const accountNumberError = validateAccountNumber(details.accountNumber)
  if (accountNumberError) {
    errors.accountNumber = accountNumberError
  }

  return Object.keys(errors).length > 0 ? errors : null
}
